import { fetchInfo, jsonPost } from 'fetch_utils';
import getIn from '@shopee/get-in';
import { jsonToQueryString } from '@shopee/url-util';
import {
  URL_ITEM_DETAIL_V4,
  URL_ITEM_SNAPSHOT,
  URL_ITEM_SNAPSHOT_V4,
  URL_BATCH_ITEM_V2,
  URL_BATCH_ITEM_V4,
  URL_ITEM_INSTALLMENT_PLAN,
  URL_ITEM_INSTALLMENT_PLAN_V4,
  URL_GET_SHIPPING_FEE_BY_ADDRESS,
  URL_GET_SHIPPING_FEE_BY_ADDRESS_V4,
  URL_GET_SHIPPING_FEE_BY_ADDRESS_V5,
} from './endPoints';
import { getLocale } from '@shopee/global-env';
import type { AddressPickerSelectedAddress } from '@shopee/flow-types';

const LOCALE = getLocale();
const ZIPCODE_FORMART_REGEX = /[^\d]/g;

export function fetchProductDetailV4(
  shopId: number,
  itemId: number,
  options?: {
    headers?: { [name: string]: any };
    apm?: { apm_pid: string; apm_p: string; apm_ts: number; apm_fs?: boolean };
  }
) {
  const params = {
    shopid: shopId,
    itemid: itemId,
    ...(options?.apm ?? {}),
  };
  return fetchInfo(URL_ITEM_DETAIL_V4 + jsonToQueryString(params), {
    headers: getIn(options, ['headers'], {}),
  });
}

export function fetchItemSnapshot(shopId: number, snapshotId: number) {
  return fetchInfo(
    URL_ITEM_SNAPSHOT + `?shopid=${shopId}&snapshotid=${snapshotId}`
  );
}

export function fetchItemSnapshotV4(shopId: number, snapshotId: number) {
  return fetchInfo(
    URL_ITEM_SNAPSHOT_V4 + `?shop_id=${shopId}&snapshot_id=${snapshotId}`
  );
}

export function fetchBatchProduct(
  itemShopIds: {
    itemid: number;
    shopid: number;
    from_flash_sale?: boolean;
  }[],
  preserveInvalid?: boolean,
  schemaFilter?: string[],
  requestVersion?: string /* Don't use this unless you know what it's doing */
) {
  const url =
    URL_BATCH_ITEM_V2 +
    (requestVersion ? `?__version__=${requestVersion}` : '');
  return jsonPost(url, {
    item_shop_ids: itemShopIds,
    preserve_invalid: preserveInvalid,
    _schema_filter: schemaFilter,
  });
}

export function fetchBatchProductV4(
  itemShopIds: {
    item_id: number;
    shop_id: number;
  }[],
  source: string
) {
  return jsonPost(URL_BATCH_ITEM_V4, {
    shop_item_ids: itemShopIds,
    source: source,
  });
}

// eslint-disable-next-line max-params
export function fetchProductInstallmentPlan(
  shopId: number,
  itemId: number,
  modelId?: number | null,
  isBFFEnabledForUser?: boolean | null,
  options?: {
    apm?: { apm_pid: string; apm_ts: number; apm_p: string; apm_fs?: boolean };
  }
) {
  const url = isBFFEnabledForUser
    ? URL_ITEM_INSTALLMENT_PLAN_V4
    : URL_ITEM_INSTALLMENT_PLAN;
  return fetchInfo(
    url +
      jsonToQueryString({
        itemid: itemId,
        shopid: shopId,
        modelid: modelId ? modelId : undefined,
        ...(options?.apm ?? {}),
      })
  );
}

export function getShippingFeeByAddress(
  shopId: number,
  itemId: number,
  address: AddressPickerSelectedAddress,
  isLite: boolean
) {
  const { state, city, district, town } = address;
  const query = jsonToQueryString({
    state,
    city,
    district: district || '',
    town: town || '',
    ...(isLite ? { is_shopee_lite: 'true' } : {}),
  });
  return fetchInfo(
    URL_GET_SHIPPING_FEE_BY_ADDRESS.replace(
      '${itemId}',
      itemId.toString(10)
    ).replace('${shopId}', shopId.toString(10)) + query
  );
}

export function getShippingFeeByAddressV4(
  shopId: number,
  itemId: number,
  address: AddressPickerSelectedAddress,
  isLite: boolean
) {
  const { state, city, district, town } = address;
  const query = jsonToQueryString({
    shopid: shopId,
    itemid: itemId,
    state,
    city,
    district: district || '',
    town: town || '',
    ...(isLite ? { is_shopee_lite: 'true' } : {}),
  });
  return fetchInfo(`${URL_GET_SHIPPING_FEE_BY_ADDRESS_V4}${query}`);
}

// eslint-disable-next-line
export function getShippingFeeByAddressV5(
  shopId: number,
  itemId: number,
  address: {
    state: string;
    city: string;
    district?: string | null;
    town?: string | null;
    zipcode?: string | null;
  },
  isLite: boolean,
  modelId?: number | null,
  options?: {
    apm?: { apm_pid: string; apm_p: string; apm_ts: number; apm_fs?: boolean };
  }
) {
  const { state, city, district, town, zipcode } = address;
  const query = jsonToQueryString({
    shopid: shopId,
    itemid: itemId,
    state,
    city,
    district: district || '',
    town: town || '',
    buyer_zipcode:
      ['MX', 'BR', 'IN', 'PL'].includes(LOCALE) && !!zipcode
        ? zipcode.replace(ZIPCODE_FORMART_REGEX, '')
        : '',
    ...(isLite ? { is_shopee_lite: 'true' } : {}),
    modelid: modelId,
    ...(options?.apm ?? {}),
  });
  return fetchInfo(`${URL_GET_SHIPPING_FEE_BY_ADDRESS_V5}${query}`);
}
